import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography,
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, Slide
} from "@mui/material";
import { Close as CloseIcon, FileDownload as ExportIcon } from "@mui/icons-material";
import { fetchData } from "../../BaseUrl";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AttendanceHistoryDialog = ({ open, onClose, student }) => {
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState({ totalClass: 0, present: 0, absent: 0 });

  const loadHistory = useCallback(async () => {
    if (!student) return;
    try {
      const result = await fetchData(`admin/attendance/student/${student.applicationId || student.id}`);
      if (result?.data) {
        setHistory(result.data.history || result.data || []);
        setSummary(result.data.summary || {
          totalClass: result.data.totalClass || 0,
          present: result.data.present || 0,
          absent: result.data.absent || 0
        });
      }
    } catch (err) {
      /* silent */
    }
  }, [student]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  if (!student) return null;

  const pct = summary.totalClass > 0 ? Math.round((summary.present / summary.totalClass) * 100) : 0;
  const circumference = 2 * Math.PI * 45;
  const dashOffset = circumference - (circumference * pct) / 100;

  const statusConfig = {
    Present: { color: "#4caf50", bg: "#e8f5e9" },
    Absent: { color: "#f44336", bg: "#fce4ec" },
    Approved: { color: "#4caf50", bg: "#e8f5e9" },
    Pending: { color: "#ff9800", bg: "#fff3e0" }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Transition}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography variant="h6" fontWeight="bold">Attendance History</Typography>
          <Typography variant="body2" color="text.secondary">
            {student.studentName || student.name || "-"} - {student.registrationNumber || student.regNo || "-"}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Circular Progress */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Box sx={{ position: "relative", width: 120, height: 120 }}>
            <svg width="120" height="120" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="45" fill="none" stroke="#e0e0e0" strokeWidth="10" />
              <circle cx="60" cy="60" r="45" fill="none" stroke="#4caf50" strokeWidth="10"
                strokeDasharray={circumference} strokeDashoffset={dashOffset}
                strokeLinecap="round" transform="rotate(-90 60 60)"
                style={{ transition: "stroke-dashoffset 0.5s ease" }} />
            </svg>
            <Box sx={{
              position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
              display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column"
            }}>
              <Typography variant="h5" fontWeight="bold" color="#4caf50">{pct}%</Typography>
              <Typography variant="caption" color="text.secondary">Attendance</Typography>
            </Box>
          </Box>
        </Box>

        {/* Summary Row */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 4, mb: 3 }}>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" fontWeight="bold">{summary.totalClass}</Typography>
            <Typography variant="caption" color="text.secondary">Total Class</Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" fontWeight="bold" color="#4caf50">{summary.present}</Typography>
            <Typography variant="caption" color="text.secondary">Present</Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" fontWeight="bold" color="#f44336">{summary.absent}</Typography>
            <Typography variant="caption" color="text.secondary">Absent</Typography>
          </Box>
        </Box>

        {/* History Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Topic</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h, idx) => {
                const st = statusConfig[h.status] || statusConfig.Pending;
                return (
                  <TableRow key={idx} hover sx={{ "&:hover": { backgroundColor: "#DAD8FC" } }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{h.date || "-"}</TableCell>
                    <TableCell>{h.topic || "-"}</TableCell>
                    <TableCell>
                      <Chip label={h.status || "Pending"} size="small"
                        sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>No attendance records found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Export Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button variant="outlined" startIcon={<ExportIcon />} sx={{ textTransform: "none" }}>
            Export
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceHistoryDialog;
